import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { HERO_SLIDES } from '../constants';
import { HeroSlide } from '../types';

interface HeroProps {
  slides?: HeroSlide[];
}

const Hero: React.FC<HeroProps> = ({ slides = HERO_SLIDES }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    // Reset slide index if slides change significantly to avoid out of bounds
    if (currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides, currentSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  if (!slides || slides.length === 0) return null;

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src={slide.image || '/assets/placeholder.svg'}
              alt=""
              aria-hidden="true"
              onError={(e) => {
                const t = e.currentTarget;
                if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
              }}
              className="w-full h-full object-cover object-center"
            />
            {/* Dark Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
          </div>

          {/* Content */}
          <div className="absolute inset-0 flex items-center justify-center text-center px-4">
            <div className="max-w-4xl space-y-6 text-white transform translate-y-8 animate-fade-in-up">
              <h1 className="font-display font-normal text-4xl md:text-6xl lg:text-7xl tracking-widest uppercase leading-tight drop-shadow-lg">
                {slide.title}
              </h1>
              <h2 className="font-heading font-light tracking-[0.2em] text-sm md:text-base animate-slide-up">
                {slide.subtitle}
              </h2>
              {slide.signOff && (
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/70">
                  {slide.signOff}
                </p>
              )}
              {slide.cta && (
                <div className="pt-8">
                  <Link
                    to={slide.link || '/shop'}
                    className="inline-block border-2 border-white px-8 py-3 md:px-12 md:py-4 font-heading font-bold tracking-widest text-sm md:text-base hover:bg-white hover:text-black transition-all duration-300 uppercase"
                  >
                    {slide.cta}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows (only when the CMS provides multiple slides) */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors p-2"
            aria-label="Previous slide"
          >
            <ChevronLeft size={48} strokeWidth={1} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors p-2"
            aria-label="Next slide"
          >
            <ChevronRight size={48} strokeWidth={1} />
          </button>

          {/* Dots Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default Hero;